import * as React from "react";
import { TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import type { ModalComponentProps } from "../../../types/modal.types";
import { postDeviceColor } from "../../../lib/apis/v1/device.service";
import { deviceErrorMessage, invalidateDeviceQueries } from "../../../lib/apis/device-cache";
import { getQueryClient } from "../../../config/queryClient.config";
import FailSnackbar from "../../ui/snackbar/fail.component";
import SuccessSnackbar from "../../ui/snackbar/success.component";
import FileDragAndDrop from "../../layout/file-drag";
import ConfirmModal from "../confirm.modal";
import { HEX_COLOR_PATTERN, normalizeHexColor } from "../../../utils/color.utils";

interface DeviceColorModalProps extends ModalComponentProps {
  deviceId: string | number;
}

export default function DeviceColorModal({ id, onClose, deviceId }: DeviceColorModalProps) {
  const [colorName, setColorName] = React.useState("");
  const [hexCode, setHexCode] = React.useState("");
  const [files, setFiles] = React.useState<File[]>([]);
  const normalizedHexCode = normalizeHexColor(hexCode);
  const invalidHexCode = hexCode.trim() !== "" && !HEX_COLOR_PATTERN.test(hexCode.trim());

  const mutation = useMutation({
    mutationFn: () => postDeviceColor(
      deviceId,
      colorName.trim(),
      normalizedHexCode || undefined,
      files[0],
    ),
    onSuccess: async () => {
      await invalidateDeviceQueries(getQueryClient());
      SuccessSnackbar("색상 옵션 추가 완료");
      onClose?.();
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  return (
    <ConfirmModal
      id={id}
      onClose={onClose}
      title="색상 옵션 추가"
      className="w-[600px]"
      rightButton={{
        title: "추가",
        disabled: mutation.isPending || !colorName.trim() || invalidHexCode,
        onClick: () => mutation.mutate(),
      }}
    >
      <div className="mt-4 flex flex-col gap-4">
        <TextField
          label="색상명*"
          value={colorName}
          disabled={mutation.isPending}
          onChange={(event) => setColorName(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="HEX 코드"
          placeholder="#000000"
          value={hexCode}
          disabled={mutation.isPending}
          error={invalidHexCode}
          helperText={invalidHexCode ? "RGB 또는 RRGGBB 형식으로 입력해 주세요. (# 선택)" : "선택 입력 (# 생략 가능)"}
          onChange={(event) => setHexCode(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <FileDragAndDrop
          fileSubject="기기 이미지 첨부 (선택)"
          fileDescription="기기 이미지를 선택하거나 올려놓아 주세요."
          uploadFiles={setFiles}
          fileData={files}
          allowedExtensions={[".jpg", ".jpeg", ".png", ".webp"]}
        />
      </div>
    </ConfirmModal>
  );
}
